import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import exoraLogo from "@/assets/exora-logo-transparent.png";

interface LuxuryLoaderProps {
  variant?: 'inline' | 'card' | 'page' | 'overlay';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const containerSizeMap = {
  inline: 'h-auto',
  card: 'min-h-[200px]',
  page: 'min-h-[400px]',
  overlay: 'min-h-full'
};

export function LuxuryLoader({ 
  variant = 'inline', 
  size = 'md',
  className 
}: LuxuryLoaderProps) {
  const logoSize = size === 'lg' ? 56 : size === 'md' ? 40 : 28;
  
  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center gap-6",
        containerSizeMap[variant],
        variant === 'overlay' && 'absolute inset-0 bg-background/80 backdrop-blur-sm z-50',
        className
      )}
      role="status" 
      aria-live="polite"
    >
      <motion.div
        className="relative flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Ambient glow */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: logoSize * 3,
            height: logoSize * 3,
            background: 'radial-gradient(circle, hsl(var(--gold) / 0.12) 0%, transparent 70%)',
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Spinning arc */}
        <motion.div
          className="absolute"
          style={{
            width: logoSize * 2.2,
            height: logoSize * 2.2,
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <defs>
              <linearGradient id="arc-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(var(--gold))" stopOpacity="0" />
                <stop offset="50%" stopColor="hsl(var(--gold))" stopOpacity="0.6" />
                <stop offset="100%" stopColor="hsl(var(--gold))" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle
              cx="50" cy="50" r="46"
              fill="none"
              stroke="url(#arc-grad)"
              strokeWidth="0.8"
              strokeDasharray="72 217"
              strokeLinecap="round"
            />
          </svg>
        </motion.div>

        {/* Logo */}
        <motion.img
          src={exoraLogo}
          alt=""
          style={{ width: logoSize, height: logoSize }}
          className="object-contain relative z-10"
          animate={{
            opacity: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>

      {/* Brand line — only for page/overlay */}
      {(variant === 'page' || variant === 'overlay') && (
        <motion.div
          className="flex flex-col items-center gap-3"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <div className="flex items-center gap-2">
            <motion.div
              className="h-px rounded-full"
              style={{ background: "hsl(var(--gold) / 0.4)", width: 24 }}
              animate={{ width: [16, 28, 16] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <span
              className="text-[10px] font-medium tracking-[0.35em] uppercase"
              style={{ color: "hsl(var(--ivory) / 0.35)" }}
            >
              Exora Club
            </span>
            <motion.div
              className="h-px rounded-full"
              style={{ background: "hsl(var(--gold) / 0.4)", width: 24 }}
              animate={{ width: [16, 28, 16] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
            />
          </div>
        </motion.div>
      )}
      
      <span className="sr-only">Loading</span>
    </div>
  );
}

// Convenient wrapper components
export function InlineLoader({ className }: { className?: string }) {
  return <LuxuryLoader variant="inline" size="sm" className={className} />;
}

export function CardLoader({ message, className }: { message?: string; className?: string }) {
  return <LuxuryLoader variant="card" size="md" className={className} />;
}

export function PageLoader({ message, className }: { message?: string; className?: string }) {
  return <LuxuryLoader variant="page" size="lg" className={className} />;
}

export function OverlayLoader({ message, className }: { message?: string; className?: string }) {
  return <LuxuryLoader variant="overlay" size="lg" className={className} />;
}
